/**
 * Pure file-selection classification and validation for the import wizard's upload phase.
 *
 * Two selection shapes are supported:
 * - A single `.zip` or spreadsheet file (existing behavior).
 * - 1-500 loose `.jpg`/`.jpeg`/`.png` files selected directly, bundled into a ZIP client-side
 *   at upload time (via `client-zip`, dynamically imported) and submitted to the existing,
 *   unchanged `POST /api/imports/photo-bundle` endpoint. PNG matches the direct-upload photo
 *   field on the media unit form and the API's accepted image/png content type — CU-86capu821.
 *
 * Covers openspec/specs/bulk-photo-import/spec.md — "Frontend — direct JPG selection without a
 * pre-made ZIP".
 */

import { BULK_PHOTO_IMPORT_MAX_ENTRIES } from '@ooh/contracts';

// Shared with the worker's authoritative enforcement via @ooh/contracts. Client-side check
// only; the worker still enforces the real limit — this just gives faster feedback.
export const MAX_PHOTO_BATCH_COUNT: number = BULK_PHOTO_IMPORT_MAX_ENTRIES;

const ACCEPTED_SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.csv'];

/** Extensions accepted for direct photo multi-select — shared with the file input's `accept`
 * attribute in upload-phase.tsx so the two never drift apart. */
export const ACCEPTED_PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png'] as const;

/** MIME types accepted for direct photo multi-select, mirrored alongside the extensions above. */
export const ACCEPTED_PHOTO_MIME = ['image/jpeg', 'image/png'] as const;

/** Combined `accept` attribute value for the photo portion of the file input. */
export const ACCEPTED_PHOTO_ACCEPT: string = [
  ...ACCEPTED_PHOTO_EXTENSIONS,
  ...ACCEPTED_PHOTO_MIME,
].join(',');

export function isZipFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip');
}

export function isPhotoFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_PHOTO_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function isAcceptedSpreadsheet(file: File): boolean {
  const normalizedName = file.name.toLowerCase();
  return ACCEPTED_SPREADSHEET_EXTENSIONS.some((ext) => normalizedName.endsWith(ext));
}

export type SingleFileErrorKey =
  | 'upload.errorSizeExceededZip'
  | 'upload.errorInvalidFileType'
  | 'upload.errorSizeExceeded';

export type SingleFileSelectionResult =
  | { ok: true; file: File }
  | { ok: false; errorKey: SingleFileErrorKey };

/** Classifies and validates a single non-JPG file selection (the existing ZIP / spreadsheet
 * flow). Pure — no state, no i18n. */
export function evaluateSingleFileSelection(
  file: File,
  limits: { zipMaxBytes: number; spreadsheetMaxBytes: number },
): SingleFileSelectionResult {
  if (isZipFile(file)) {
    if (file.size > limits.zipMaxBytes) {
      return { ok: false, errorKey: 'upload.errorSizeExceededZip' };
    }
    return { ok: true, file };
  }
  if (!isAcceptedSpreadsheet(file)) {
    return { ok: false, errorKey: 'upload.errorInvalidFileType' };
  }
  if (file.size > limits.spreadsheetMaxBytes) {
    return { ok: false, errorKey: 'upload.errorSizeExceeded' };
  }
  return { ok: true, file };
}

export type PhotoBatchErrorKey =
  | 'upload.errorMixedFileTypes'
  | 'upload.errorTooManyPhotos'
  | 'upload.errorSizeExceededZip';

export type PhotoBatchValidationResult =
  | { ok: true; files: File[] }
  | { ok: false; errorKey: PhotoBatchErrorKey; errorParams?: Record<string, unknown> };

/** Validates a candidate JPG/PNG multi-select: no mixed file types, count within the worker's
 * per-job cap, and combined size within the photo-bundle ZIP size cap. Pure — no state. */
export function validatePhotoBatchSelection(
  files: File[],
  maxTotalBytes: number,
): PhotoBatchValidationResult {
  const nonPhotoCount = files.filter((f) => !isPhotoFile(f)).length;
  if (nonPhotoCount > 0) {
    return { ok: false, errorKey: 'upload.errorMixedFileTypes' };
  }
  if (files.length > MAX_PHOTO_BATCH_COUNT) {
    return {
      ok: false,
      errorKey: 'upload.errorTooManyPhotos',
      errorParams: { max: MAX_PHOTO_BATCH_COUNT },
    };
  }
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  if (totalSize > maxTotalBytes) {
    return { ok: false, errorKey: 'upload.errorSizeExceededZip' };
  }
  return { ok: true, files };
}

/** Bundles loose JPG files into a single ZIP in the browser, reusing the existing `client-zip`
 * dependency (already used for photo-download bundling elsewhere in apps/web). The resulting
 * File is submitted to the unchanged /imports/photo-bundle endpoint, identical to a
 * user-provided .zip upload. */
export async function buildPhotoZip(files: File[]): Promise<File> {
  const { downloadZip } = await import('client-zip');
  const entries = files.map((file) => ({ name: file.name, input: file }));
  const blob = await downloadZip(entries).blob();
  return new File([blob], 'photos.zip', { type: 'application/zip' });
}
